import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const SKIP_FIELDS = new Set(['updatedAt', 'createdAt', 'timestamp']);

function formatValue(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'string') return val === '' ? '(empty)' : val;
  if (typeof val === 'number') return String(val);
  // Firestore timestamp object
  if (val._seconds !== undefined) {
    return new Date(val._seconds * 1000).toLocaleDateString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  }
  // Firestore Timestamp with toDate()
  if (typeof val.toDate === 'function') {
    return val.toDate().toLocaleDateString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  }
  return JSON.stringify(val);
}

function buildDiff(prev, next) {
  if (!prev || !next || typeof prev !== 'object' || typeof next !== 'object') return null;
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  const changes = [];
  for (const key of keys) {
    if (SKIP_FIELDS.has(key)) continue;
    const a = formatValue(prev[key]);
    const b = formatValue(next[key]);
    if (a !== b) changes.push({ key, from: a, to: b });
  }
  return changes;
}

const FIELD_LABELS = {
  name: 'Name',
  status: 'Status',
  ready: 'Ready',
  ignored: 'Ignored',
  note: 'Note',
  deliveryPatient: 'Delivery',
  deliveryNote: 'Delivery Note',
  cycleLength: 'Cycle (days)',
  nextDue: 'Next Due',
  prepStart: 'Prep Start',
  lastPickup: 'Last Pickup',
};

function DiffCell({ prev, next, action }) {
  // Non-patient logs or empty values
  if (!prev && !next) return <span style={{ color: '#94A3B8' }}>—</span>;
  if (typeof prev === 'string' && typeof next === 'string' && prev === '' && next === '') {
    return <span style={{ color: '#94A3B8' }}>—</span>;
  }

  const changes = buildDiff(prev, next);
  if (!changes || changes.length === 0) {
    if (action === 'PATIENT_CREATED' && next?.name) {
      return <span style={{ color: '#0F172A' }}>{next.name}</span>;
    }
    return <span style={{ color: '#94A3B8' }}>No field changes</span>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {changes.map(({ key, from, to }) => (
        <div key={key} style={{ fontSize: 11, lineHeight: 1.4 }}>
          <span style={{ color: '#64748B', fontWeight: 600 }}>
            {FIELD_LABELS[key] || key}:
          </span>{' '}
          <span style={{ color: '#94A3B8', textDecoration: 'line-through' }}>{from}</span>
          {' → '}
          <span style={{ color: '#0F172A', fontWeight: 500 }}>{to}</span>
        </div>
      ))}
    </div>
  );
}

export default function ActivityLogPage() {
  const { pharmacy } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pharmacy) return;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, 'pharmacies', pharmacy.id, 'activityLogs'),
            orderBy('timestamp', 'desc'),
            limit(200)
          )
        );
        setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, [pharmacy]);

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Activity Log</h1>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: '#94A3B8' }}>Audit trail — read only</p>
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>No activity recorded yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Time', 'User', 'Initials', 'Action', 'Changes'].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderBottom: '1px solid #F1F5F9'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(l => {
                const ts = l.timestamp?.toDate ? l.timestamp.toDate()
                  : l.timestamp?._seconds ? new Date(l.timestamp._seconds * 1000)
                  : null;
                return (
                  <tr key={l.id} style={{ borderBottom: '1px solid #F8FAFC' }}>
                    <td style={{ padding: '10px 16px', color: '#64748B', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {ts ? ts.toLocaleString('en-CA') : '—'}
                    </td>
                      <td style={{ padding: '10px 16px', color: '#0F172A', fontWeight: 500 }}>
                        {l.displayName || l.actorEmail || l.userId || '—'}
                      </td>
                      <td style={{ padding: '10px 16px', fontWeight: 600 }}>
                        {l.staffInitials || '—'}
                      </td>
                      <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        background: l.action?.includes('CREATED') ? '#DCFCE7'
                          : l.action?.includes('DELETED') ? '#FEE2E2'
                          : l.action?.includes('UPDATED') ? '#DBEAFE'
                          : '#F1F5F9',
                        color: l.action?.includes('CREATED') ? '#16A34A'
                          : l.action?.includes('DELETED') ? '#DC2626'
                          : l.action?.includes('UPDATED') ? '#1768C4'
                          : '#64748B',
                      }}>
                        {l.action || '—'}
                      </span>
                      {(l.newValue?.name || l.previousValue?.name) && (
                        <div style={{ fontSize: 11, color: '#64748B', marginTop: 3 }}>
                          {l.newValue?.name || l.previousValue?.name}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <DiffCell prev={l.previousValue} next={l.newValue} action={l.action} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
